import { Input, InputProps, InputVariant } from '@pluxee-design-system/core';
import { SkippableBaseControlProps } from '@pluxee-design-system/react-hook-form';
import { ChangeEventHandler, useCallback } from 'react';
import { useController } from 'react-hook-form';
import { getVariant } from './utils';

interface CompanyIdControlProps
  extends Omit<InputProps, 'name' | 'type' | 'value' | 'variant'>,
    SkippableBaseControlProps {
  name: string;
  readonly?: boolean;
  format?: string;
}

export const formatValue = (rawValue: string, format?: string) => {
  if (rawValue === '') return '';
  const hasSeparators = format?.includes('.');

  let value = rawValue
    // strip alphabet letters
    .replace(/[A-Za-z]/g, '');
  // replace dot mark with placeholder "M"
  if (hasSeparators) value = value.replace(/\./g, 'M');

  // strip non-numeric letters except "M"
  if (hasSeparators) value = value.replace(/[^\dM]/g, '').replace(/M/g, '.');
  else value = value.replace(/[^\d]/g, '');

  if (format) {
    const lastFormatPos = format.length;
    const lastValuePos = value.length;
    let nextFormatPos = 0;
    let nextValuePos = 0;
    const word = format.split('');
    let lastFillPos = 0;

    while (nextFormatPos < lastFormatPos && nextValuePos < lastValuePos) {
      const symbol = format[nextFormatPos];
      if (symbol === '#') {
        if (value[nextValuePos] !== '.') {
          word[nextFormatPos] = value[nextValuePos];
          nextFormatPos += 1; // move to next format position
          lastFillPos = nextFormatPos;
        }
        // else - stay in current format position
        nextValuePos += 1; // move to next value position
      } else if (symbol === '.') {
        if (value[nextValuePos] === '.') {
          nextValuePos += 1; // move to next value position
          lastFillPos = nextFormatPos + 1;
        }
        // else - stay in current format position
        nextFormatPos += 1; // move to next format position
      }
    }
    return word.join('').substring(0, lastFillPos);
  }

  return value;
};

const CompanyIdControl = ({
  control,
  disabled,
  isDisabled,
  format,
  readonly,
  isReadOnly,
  mandatory,
  name,
  rules,
  skipSuccess,
  skipVariant,
  onChange: _,
  ...inputProps
}: CompanyIdControlProps) => {
  const { field, fieldState, formState } = useController({
    name,
    control,
    rules,
  });
  const { onChange } = field;
  const { error } = fieldState;
  const variant = getVariant(
    formState,
    fieldState,
    isReadOnly ?? readonly,
    skipSuccess,
    skipVariant,
  );

  // handle any format to proper format
  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const value = formatValue(event.target.value, format);
      onChange(value);
    },
    [format, onChange],
  );

  return (
    <Input
      mandatory={mandatory}
      name={field.name}
      onBlur={inputProps.onBlur ?? field.onBlur}
      onChange={handleChange}
      value={field.value}
      {...inputProps}
      placeholder={
        variant === InputVariant.READONLY
          ? inputProps.readonlyEmptyPlaceholder || '--'
          : inputProps.placeholder ?? inputProps.title
      }
      disabled={variant !== InputVariant.READONLY && (disabled ?? isDisabled)}
      variant={variant}
      helpText={error?.message ?? inputProps.helpText}
      format={undefined}
    />
  );
};

export default CompanyIdControl;
